import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Loader from 'components/molecules/Loader';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import _ from 'lodash';
import { capitalizeFirstLetterOfEachWord, getAdditionalDataOnCopyClick, getConfig } from 'utils/helperMethods';
import NoDataForSelection from 'components/atoms/NoDataForSelection';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PVM_ANALYSIS_FACTOR_CLICKED,
    PVM_ANALYSIS_PLAN_SELECTED,
    PVM_ANALYSIS_MONTH_SELECTED,
    PVM_ANALYSIS_COPY_SCREENSHOT,
    PVM_ANALYSIS_DOWNLOAD_DATA,
} from 'utils/mixpanelEventNames';
import DownSvg from 'assets/svg/Down';
import styled from '@emotion/styled';
import { Select, Table } from 'antd';
import tw from 'twin.macro';
import { getPVMSalesAttributionColumns, getPVMTableDataColumns } from 'utils/pvmAnalysisColumns';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import PVMAnalysisDrillDownContainer from 'components/organism/PVMAnalysis/PVMAnalysisDrilldownContainer';
import GrowthAnalysisTable from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import { useFetchFeatureGroupsData, useFetchPlansDropdownValues, useFetchYearDropdownValues } from 'hooks/pvmAnalysis';
import { useFetchFeatureTableData } from 'hooks/pvmAnalysis/useFetchFeatureTableData';
import { getPvmImpactAnalysisChartExportData, getPvmImpactExportData } from 'parser/graphToExcelDownloadParser';
import { tableStyle } from 'components/organism/GrowthAnalysis/SalesAttributionChart/SalesAttributionChart';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { ConfigKeys } from 'utils/Enums';

export type salesDataType = {
    chartData: any | null;
    tableData: any | null;
    loading: boolean;
    error: any;
    count: number;
};

export type salesTileKeyType = 'sales' | 'incremental' | 'promo';

export type salesTileItemType = {
    key: salesTileKeyType;
    label: string;
};
export const subHeaderClass = 'm-0 font-medium font-lg text-subHeaderColor';
export const yAxisLabelClass = 'text-greyFont font-xs font-medium';
export const graphItemContainerClass = 'p-4 mb-4 border border-borderLinePurpule rounded';

export const CustomSelect = styled(Select)`
    ${tw`border border-purpleFont rounded text-[13px] text-headerColor`}
    .ant-select-selector {
        height: 100% !important;
        display: flex;
        align-items: center;
        box-shadow: none !important;
        border: none !important;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
`;

export const tableFirstColumnHeader = 'Months';
const startDateLabel = 'Start Date';
const endDateLabel = 'End Date';
const PVMAnalysis = () => {
    const {
        filterNullError,
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodLoading,
        idForRestOfComponentLoading,
        allCheckedAttribute,
        timeSelectionLabel,
        setHideGlobalTimeFilterComponent,
    } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const { salesAttributionChart } = getConfig('pvmAnalysis', appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const [comparisonValue, setComparisonValue] = useState<any>('');
    const [selectedStartMonth, setSelectedStartMonth] = useState<any>();
    const [selectedEndMonth, setSelectedEndMonth] = useState<any>();
    const [drillDownItem, setDrillDownItem] = useState<any>(null);
    const [level2DrillDownItem, setLevel2DrillDownItem] = useState<any>(null);
    const varianceChartContainerRef = useRef(null);

    const refTable = useRef(null);

    // for pvm analysis we want to hide time period filter selection in the global filter
    useEffect(() => {
        setHideGlobalTimeFilterComponent(true);
        // resetting the state
        return () => {
            setHideGlobalTimeFilterComponent(false);
        };
    }, []);

    const { data: plansDropdownData, loading: plansDropdownLoading } = useFetchPlansDropdownValues({
        setComparisonValue,
    });

    const uniquePlanNames: any = useMemo(() => {
        let result = null;
        if (plansDropdownData) {
            result = Array.from(new Set(plansDropdownData?.flatMap((i: any) => i.apiParam)));
        }
        return result;
    }, [plansDropdownData]);

    const { data: yearDropdownData, loading: yearDropdownLoading } = useFetchYearDropdownValues({
        setSelectedStartMonth,
        setSelectedEndMonth,
        plans: uniquePlanNames,
        queryConfig: {
            enabled: !plansDropdownLoading && uniquePlanNames?.length > 0,
        },
    });

    const commonVariables = useMemo(() => {
        setDrillDownItem(null);
        if (selectedStartMonth?.value && !idForRestOfComponentLoading) {
            const [start_month, start_year] = selectedStartMonth.value.split('-').map(Number);
            const [end_month, end_year] = selectedEndMonth.value.split('-').map(Number);
            return {
                start_month,
                start_year,
                end_month,
                end_year,
                feature_name: 'pvm',
                comparison_plans: comparisonValue?.apiParam,
                filters: idForRestOfComponent,
            };
        }
    }, [selectedStartMonth, selectedEndMonth, comparisonValue, idForRestOfComponentLoading, idForRestOfComponent]);

    const {
        data: featureGroupData,
        loading: featureGroupDataLoading,
        error: featureGroupDataError,
    } = useFetchFeatureGroupsData({
        queryKey: ['pvm-feature-groups-data', commonVariables],
        variables: commonVariables,
        queryConfig: {
            enabled: !idForRestOfComponentLoading && !!selectedStartMonth?.value && !!comparisonValue?.value,
        },
        plansLabel: comparisonValue?.label?.split(' vs '),
    });

    const {
        data: featureTableData,
        loading: featureTableLoading,
        error: featureTableError,
    } = useFetchFeatureTableData({
        queryKey: [
            'pvm-feature-table-data',
            commonVariables,
            drillDownItem ? drillDownItem?.apiParamKey : 'price_impact',
        ],
        variables: {
            ...commonVariables,
            feature_group_name: drillDownItem ? drillDownItem?.apiParamKey : 'price_impact',
        },
        queryConfig: {
            enabled: !idForRestOfComponentLoading && !!selectedStartMonth?.value,
        },
    });

    const memoisedTableColumns = useMemo(() => {
        let columns: any[] = [];
        if (featureTableData?.length > 0) {
            columns = getPVMTableDataColumns(
                featureTableData,
                tableFirstColumnHeader,
                decimalForNonPercentage,
                decimalForPercentage,
            );
        }
        return columns;
    }, [featureTableData]);

    const exportData = useMemo(() => {
        return getPvmImpactExportData(featureTableData);
    }, [featureTableData]);

    const containerWidth = useContainerWidth({ containerId: 'saleAttributionTable', buffer: 16 * 2 + 6 }); //buffer calculation (16*2 gives the padding and 6 subtracting borders of 1 px)

    const memoisedPVMSalesAttributionColumns = useMemo(() => {
        const columns: any[] = getPVMSalesAttributionColumns(
            [salesAttributionChart?.colorList, salesAttributionChart?.colorListType2],
            featureGroupData?.data,
            'attributionChart',
            {
                max: featureGroupData?.maxVal,
                min: featureGroupData?.minVal,
                headerTitle: featureGroupData?.headers,
                barLength: 7,
                onClick: (item: any) => {
                    trackEventMixpanel(PVM_ANALYSIS_FACTOR_CLICKED, {
                        PVMFactorName: capitalizeFirstLetterOfEachWord(item, '_'),
                    });
                    setDrillDownItem(
                        !item ? null : { apiParamKey: item, category: capitalizeFirstLetterOfEachWord(item, '_') },
                    );
                },
                selected: drillDownItem?.apiParamKey,
                width: containerWidth,
                attributeTypeIndex: 0,
                config: appConfig,
            },
        );
        return columns;
    }, [drillDownItem, featureGroupData]);

    const memoisedEndDates = useMemo(() => {
        const endMonths: any[] = [];
        yearDropdownData?.every((month: any) => {
            endMonths.push(month);
            return month.value !== selectedStartMonth.value;
        });
        setSelectedEndMonth(endMonths[0]);
        return endMonths;
    }, [selectedStartMonth]);

    const exportTimePeriodHeader = useMemo(() => {
        const labelHeader = formatTimePeriodExport(
            { label: startDateLabel, periodLabel: selectedStartMonth?.label },
            { label: endDateLabel, periodLabel: selectedEndMonth?.label },
        );
        return labelHeader;
    }, [selectedStartMonth, selectedEndMonth]);

    return (
        <>
            {/* PVM ANALYSIS CONTAINER */}
            <div
                className={`border border-borderLinePurpule bg-white p-4  rounded-lg  overflow-hidden          
            `}
            >
                {filterNullError ? (
                    <NoDataForSelection />
                ) : (
                    <div>
                        <div className="flex items-center">
                            <h2 className="font-medium font-xl mb-2 text-headerColor">PVM Analysis</h2>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex flex-col">
                                <CustomSelect
                                    style={{ marginTop: 20 }}
                                    suffixIcon={<DownSvg />}
                                    disabled={plansDropdownLoading}
                                    value={comparisonValue?.value}
                                    options={plansDropdownData}
                                    className="dropdown-height dropdown-width-multi-select overflow-hidden uniqSelPVMActual/PlanDropdown"
                                    onChange={(val: any, item: any) => {
                                        trackEventMixpanel(PVM_ANALYSIS_PLAN_SELECTED, {
                                            PVMCategoryDropDownValue: item.label,
                                        });
                                        setComparisonValue(item);
                                    }}
                                />
                            </div>

                            <div className="flex gap-2">
                                <div className="flex flex-col">
                                    <span className="font-xs text-fontGray">{startDateLabel}</span>
                                    <CustomSelect
                                        style={{ marginRight: 16 }}
                                        suffixIcon={<DownSvg />}
                                        disabled={yearDropdownLoading}
                                        value={selectedStartMonth?.value}
                                        options={yearDropdownData}
                                        className="dropdown-height dropdown-width overflow-hidden uniqSelPVMMonthDropdown"
                                        onChange={(val: any, item: any) => {
                                            trackEventMixpanel(PVM_ANALYSIS_MONTH_SELECTED, {
                                                PVMMonthSelectedStartValue: item.label,
                                            });
                                            setSelectedStartMonth(item);
                                        }}
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <span className="font-xs text-fontGray">{endDateLabel}</span>
                                    <CustomSelect
                                        style={{ marginRight: 16 }}
                                        suffixIcon={<DownSvg />}
                                        disabled={yearDropdownLoading}
                                        value={selectedEndMonth?.value}
                                        options={memoisedEndDates}
                                        className="dropdown-height dropdown-width overflow-hidden uniqSelPVMMonthDropdown"
                                        onChange={(val: any, item: any) => {
                                            trackEventMixpanel(PVM_ANALYSIS_MONTH_SELECTED, {
                                                PVMMonthSelectedEndValue: item.label,
                                            });
                                            setSelectedEndMonth(item);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {allDimAndAttributeTypeList?.loading ||
                        timePeriodLoading ||
                        yearDropdownLoading ||
                        plansDropdownLoading ? (
                            <div
                                style={{
                                    height: '60vh',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                                className={graphItemContainerClass}
                            >
                                <Loader />
                            </div>
                        ) : (
                            <>
                                <div
                                    className={`flex flex-col !p-0 ${graphItemContainerClass}  `}
                                    id="pvm-analysis-sales-chart"
                                >
                                    <div className="flex items-center gap-1 justify-between my-4">
                                        <p className={`${subHeaderClass} ml-2`}>PVM Impact Analysis Chart</p>
                                        <span className="flex gap-2 mr-2">
                                            <span className="flex gap-2">
                                                <CopyToClipBoard
                                                    mixpanelEvent={() =>
                                                        trackEventMixpanel(PVM_ANALYSIS_COPY_SCREENSHOT, {
                                                            PVMFacterName: 'PVM Impact Analysis Chart',
                                                        })
                                                    }
                                                    removeScrollbar
                                                    elementClassToRemoveScrollbar="ant-table-body"
                                                    refOfComponent={varianceChartContainerRef}
                                                />
                                                <DownloadExcel
                                                    mixpanelEvent={() =>
                                                        trackEventMixpanel(PVM_ANALYSIS_DOWNLOAD_DATA, {
                                                            PVMFacterName: 'PVM Impact Analysis Chart',
                                                        })
                                                    }
                                                    customTimePeriodHeader={exportTimePeriodHeader}
                                                    title={'PVM Impact Analysis Chart'}
                                                    excelData={getPvmImpactAnalysisChartExportData(
                                                        featureGroupData?.data,
                                                        featureGroupData?.headers,
                                                    )}
                                                    headers={featureGroupData?.headers}
                                                />
                                            </span>
                                        </span>
                                    </div>

                                    <Table
                                        ref={varianceChartContainerRef}
                                        id="saleAttributionTable"
                                        className={tableStyle}
                                        pagination={false}
                                        loading={featureGroupDataLoading}
                                        columns={memoisedPVMSalesAttributionColumns}
                                        dataSource={featureGroupData?.data || []}
                                        size="small"
                                        scroll={{ x: 1000 }}
                                        bordered={true}
                                        locale={{
                                            emptyText: featureTableError ? (
                                                <ErrorBox msg={String(featureTableError)} />
                                            ) : (
                                                ''
                                            ),
                                        }}
                                    />
                                </div>
                                {/* RENDER DRILLDOWN CHARTS */}
                                {drillDownItem && (
                                    <PVMAnalysisDrillDownContainer
                                        exportTimePeriodHeader={exportTimePeriodHeader}
                                        drillDownItem={drillDownItem}
                                        commonVariables={commonVariables}
                                        level2DrillDownItem={level2DrillDownItem}
                                        setLevel2DrillDownItem={setLevel2DrillDownItem}
                                        decimalForNonPercentage={decimalForNonPercentage}
                                        decimalForPercentage={decimalForPercentage}
                                    />
                                )}

                                {/* GROUP TABLE DATA */}
                                {!level2DrillDownItem && (
                                    <>
                                        <div className="flex justify-between items-center mt-2 mb-1">
                                            <p className={subHeaderClass}>
                                                {drillDownItem?.category || 'Price Impact'} Data Table
                                            </p>
                                            <div className="flex justify-end mb-2 mt-4">
                                                <span className="flex gap-2">
                                                    <CopyToClipBoard
                                                        refOfComponent={refTable}
                                                        elementClassToRemoveScrollbar="ant-table-body"
                                                        removeScrollbar
                                                    />

                                                    <DownloadExcel
                                                        customTimePeriodHeader={exportTimePeriodHeader}
                                                        mixpanelEvent={() =>
                                                            trackEventMixpanel(PVM_ANALYSIS_DOWNLOAD_DATA, {
                                                                PVMGlobalDataTable: `${
                                                                    drillDownItem?.category || 'Price Impact'
                                                                } Data Table`,
                                                            })
                                                        }
                                                        title={`${
                                                            drillDownItem?.category || 'Price Impact'
                                                        } Data Table`}
                                                        excelData={exportData.result}
                                                        headers={exportData.headers}
                                                    />
                                                </span>
                                            </div>
                                        </div>

                                        <div ref={refTable}>
                                            <GrowthAnalysisTable
                                                bordered
                                                columns={memoisedTableColumns}
                                                data={featureTableData || []}
                                                tableLoading={featureTableLoading}
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default PVMAnalysis;

function formatTimePeriodExport(
    startPeriod: { label: string; periodLabel: string },
    endPeriod: { label: string; periodLabel: string },
) {
    return [`${startPeriod.label}: ${startPeriod.periodLabel}`, `${endPeriod.label}: ${endPeriod.periodLabel}`];
}
